import FormControl from '@mui/material/FormControl';
import type { SxProps, Theme } from '@mui/material/styles';
import type { AnyFieldMetaBase } from '@tanstack/react-form';
import { createFormHook } from '@tanstack/react-form';

import {
  fieldContext,
  formContext,
  useFieldContext,
} from '~/common/form/formContexts';
import MultiSelect from '~/common/mui/Inputs/Selects/MultiSelect/MultiSelect';

export interface SelectOption {
  label: string;
  value: any; // intentionally broad — feature modules cast to their specific type
}

export function buildFieldErrors(
  fieldId: string,
  errorMessages: unknown[],
): Record<string, { message: string }> {
  if (errorMessages.length === 0) return {};
  return { [fieldId]: { message: errorMessages[0] as string } };
}

type SelectFieldProps = {
  id: string;
  label: string;
  options: SelectOption[];
  sx?: SxProps<Theme>;
  onChange?: (option: SelectOption | null) => void;
  isOptionEqualToValue?: (option: SelectOption, value: SelectOption) => boolean;
  'data-tracking'?: string;
  'data-testid'?: string;
};

const defaultIsOptionEqualToValue = (opt: SelectOption, val: SelectOption) =>
  opt?.value === val?.value;

// eslint-disable-next-line react-refresh/only-export-components
function SelectField({
  id,
  label,
  options,
  sx,
  onChange,
  isOptionEqualToValue,
  ...dataProps
}: Readonly<SelectFieldProps>) {
  const field = useFieldContext<SelectOption | null>();
  const errors = buildFieldErrors(id, field.state.meta.errors);

  const clearErrors = () =>
    field.setMeta((prev: AnyFieldMetaBase) => ({ ...prev, errorMap: {} }));

  return (
    <FormControl error={field.state.meta.errors.length > 0} sx={sx}>
      <MultiSelect
        id={id}
        label={label}
        value={field.state.value}
        options={options}
        onChange={(_event: unknown, option: SelectOption | null) => {
          field.handleChange(option);
          onChange?.(option);
        }}
        getOptionLabel={(option: SelectOption | null) => option?.label ?? ''}
        isOptionEqualToValue={
          isOptionEqualToValue ?? defaultIsOptionEqualToValue
        }
        errors={errors}
        clearErrors={clearErrors}
        disableClearable
        {...dataProps}
      />
    </FormControl>
  );
}

export const { useAppForm, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    /**
     * Single-select dropdown backed by `MultiSelect`.
     * Handles its own error display and error-clearing.
     * Pass an `onChange` prop for cross-field side-effects.
     */
    SelectField,
  },
  formComponents: {},
});

export { useStore } from '@tanstack/react-form';

export interface ClassifyFormData {
  documentCategory: SelectOption | null;
  documentType: SelectOption | null;
  [key: string]: SelectOption | SelectOption[] | null | undefined;
}

export type ClassifyFieldValue = SelectOption | SelectOption[] | null;
